import Button from '@mui/material/Button';
import { LoadingButton } from '@mui/lab';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormLabel from '@mui/material/FormLabel';
import IconButton from '@mui/material/IconButton';
import SvgIcon from '@mui/material/SvgIcon';
import TextField from '@mui/material/TextField';
import { Close as CloseIcon } from '@opengov/react-capital-assets';
import { capitalize } from 'lodash';
import React, { useState } from 'react';
import { createPortal } from 'react-dom';

import { styles } from './ConfirmationModal.styles';

export type IConfirmationModalProps = {
  title: string;
  description?: React.ReactNode;
  dataTest?: string;
  confirmationText: string;
  cancelText?: string;
  actionPending?: boolean;
  actionDisabled?: boolean;
  confirmationRequired?: boolean;
  confirmationButtonVariant?: 'primary' | 'destructive';
  onCancel?(): void;
  onProceed?(): void;
};

export const ConfirmationModalView = (props: IConfirmationModalProps) => {
  const [confirmText, setConfirmText] = useState('');
  const {
    title,
    description,
    dataTest,
    actionPending,
    actionDisabled,
    confirmationButtonVariant = 'primary',
    confirmationRequired,
    confirmationText,
    cancelText,
    onCancel,
    onProceed,
  } = props;

  const expectedConfirmText = confirmationText.toUpperCase();
  const disabled = actionDisabled || actionPending || (confirmationRequired && confirmText !== expectedConfirmText);

  return (
    <Dialog open={true} onClose={onCancel} aria-labelledby="confirmation-modal-title" data-test={dataTest} PaperProps={{ sx: styles.dialogPaper, ...(dataTest && { 'data-test': `dialog-${dataTest}-container` }) }}>
      <DialogTitle id="confirmation-modal-title" sx={styles.dialogTitle}>
        {title}
        <IconButton aria-label="close" onClick={onCancel} sx={styles.closeButton}>
          <SvgIcon component={CloseIcon} inheritViewBox aria-hidden={true} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={styles.dialogContent}>
        {description}
        {confirmationRequired && (
          <>
            <FormLabel htmlFor="confirmation-input" sx={styles.formLabel}>{`Type "${expectedConfirmText}" to confirm`}</FormLabel>
            <TextField
              id="confirmation-input"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="Type here..."
              required={confirmationRequired}
              size="small"
              sx={styles.textField}
              inputProps={{ className: 'no-format', 'data-test': 'confirmationInput' } as React.InputHTMLAttributes<HTMLInputElement>}
            />
          </>
        )}
      </DialogContent>
      <DialogActions sx={styles.dialogActions}>
        <Button
          data-test="cancelConfirmationButton"
          variant="outlined"
          color="secondary"
          size="small"
          onClick={onCancel}
        >
          {cancelText || 'Cancel'}
        </Button>
        <LoadingButton
          data-test="proceedConfirmationButton"
          variant="contained"
          color={confirmationButtonVariant === 'destructive' ? 'error' : 'primary'}
          size="small"
          disabled={disabled}
          loading={actionPending}
          onClick={onProceed}
        >
          {capitalize(confirmationText)}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export const ConfirmationModal = (props: IConfirmationModalProps) => {
  return createPortal(<ConfirmationModalView {...props} />, document.getElementById('app'));
};
